import type { TeamFormResult, UpcomingFixture } from './types'
import { getGamesByGameweek, getActualResultsByGameweek } from './seasonData'
import { findTeamId, type TeamId } from './teams'

/**
 * `TEAM_NAME_ALIASES`/`canonicalTeam` lived here. Reconciling spellings is now
 * the registry's job (`src/teams.ts`) and fixtures carry team ids, so there is nothing left to
 * normalize on lookup — callers that still hold a display name go through `findTeamId` once, at
 * the edge.
 */

export function getRecentForm(
  team: TeamId | string | null | undefined,
  beforeGameweek: number,
  count = 5
): TeamFormResult[] {
  const id = findTeamId(team)
  if (id === null) return []
  const results: TeamFormResult[] = []

  for (let gw = beforeGameweek - 1; gw >= 1 && results.length < count; gw--) {
    const gwResults = getActualResultsByGameweek()[gw] ?? {}
    const game = (getGamesByGameweek()[gw] ?? []).find(
      (g) => g.homeTeam === id || g.awayTeam === id
    )
    if (!game) continue

    const score = gwResults[game.id]
    if (!score) continue

    const isHome = game.homeTeam === id
    const { homeScore, awayScore } = score
    if (homeScore === awayScore) {
      results.push('D')
    } else if (isHome === (homeScore > awayScore)) {
      results.push('W')
    } else {
      results.push('L')
    }
  }

  return results.reverse()
}

export function getUpcomingGames(
  team: TeamId | string | null | undefined,
  fromGameweek: number,
  count = 5
): UpcomingFixture[] {
  const id = findTeamId(team)
  if (id === null) return []
  const maxGameweek = Math.max(...Object.keys(getGamesByGameweek()).map(Number))
  const fixtures: UpcomingFixture[] = []

  for (let gw = fromGameweek; gw <= maxGameweek && fixtures.length < count; gw++) {
    const game = (getGamesByGameweek()[gw] ?? []).find(
      (g) => g.homeTeam === id || g.awayTeam === id
    )
    if (!game) continue

    const isHome = game.homeTeam === id
    fixtures.push({
      opponent: isHome ? game.awayTeam : game.homeTeam,
      homeAway: isHome ? 'H' : 'A',
      kickoff: game.kickoff,
      gameweek: game.gameweek,
    })
  }

  return fixtures
}
